import React from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";

import { DataGridPro } from "@mui/x-data-grid-pro";
import { LicenseInfo } from "@mui/x-license-pro";
import { GridRowModes, GridActionsCellItem } from "@mui/x-data-grid";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";

import CategoricalEditCell from "./CategoricalEditCell";
import CategoricalEditCellMultiSelect from "./CategoricalEditCellMultiselect";
import RenderConfirmDelete from "./RenderConfirmDelete";
import SelectBasinPeople from "./SelectBasinPeople";
import SelectProjectSites from "./SelectProjectSites";
import SelectProjectPeople from "./SelectProjectPeople";
import SelectSiteParameters from "./SelectSiteParameters";
import Toolbar from "./Toolbar";
import CustomFooter from "./CustomFooter";

import { entities } from "../../config/config.dataExplorer";
import { API_URL } from "../../config/config.data";
import { getData } from "../../store/dataSlice";

const materialUIKey = process.env.REACT_APP_MATERIAL_UI_TOKEN;
LicenseInfo.setLicenseKey(materialUIKey);

const SNACKBAR_AUTO_HIDE_DURATION = 2000;

const DataEditor = ({ showFilters }) => {
  const dispatch = useDispatch();
  const selectedEntityId = useSelector(
    (state) => state.dataExplorer.selectedEntity
  );
  const entityConfig = entities[selectedEntityId];
  const filters = useSelector(
    (state) => state.dataExplorer.filters[selectedEntityId]
  );
  const data = useSelector((state) => state.data[selectedEntityId]?.data);

  const getFilterItems = () =>
    Object.keys(filters)
      .map((filterId, index) => {
        const filterConfig = entityConfig?.filterOptions?.find(
          (filter) => filter.id === filterId
        );
        if (!filterConfig) {
          return [];
        }
        return filters[filterId]?.map((value) => ({
          id: index,
          field: filterId,
          operator: filterConfig.operator,
          value: value.trim(),
        }));
      })
      .flat();

  const [deleteId, setDeleteId] = React.useState(null);
  const [filterModel, setFilterModel] = React.useState({
    items: getFilterItems(filters),
  });
  const [rowModesModel, setRowModesModel] = React.useState({});
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [snackbarAutoHideDuration, setSnackbarAutoHideDuration] =
    React.useState(null);

  const sortKey = entityConfig?.sortKey || "name";
  const rows = [...(data || [])]
    ?.sort((d1, d2) =>
      d1[sortKey] && d2[sortKey]
        ? d1[sortKey].localeCompare(d2[sortKey])
        : d1.id > d2.id
        ? -1
        : 1
    )
    .map((dataset, index) => ({
      ...dataset,
    }));

  let columns = [...(entityConfig?.columnConfig || [])]
    .filter((column) => Object.keys(data?.[0] || {}).includes(column.field))
    .map((column) => {
      if (column.renderEditCellComponent) {
        switch (column.renderEditCellComponent) {
          case "SelectProjectPeople":
            return {
              ...column,
              renderEditCell: (params) => (
                <SelectProjectPeople
                  {...params}
                  column={column}
                  rowModesModel={rowModesModel}
                  setRowModesModel={setRowModesModel}
                />
              ),
            };
          case "SelectProjectSites":
            return {
              ...column,
              renderEditCell: (params) => (
                <SelectProjectSites
                  {...params}
                  column={column}
                  rowModesModel={rowModesModel}
                  setRowModesModel={setRowModesModel}
                />
              ),
            };
          case "SelectBasinPeople":
            return {
              ...column,
              renderEditCell: (params) => (
                <SelectBasinPeople
                  {...params}
                  column={column}
                  rowModesModel={rowModesModel}
                  setRowModesModel={setRowModesModel}
                />
              ),
            };
          case "SelectSiteParameters":
            return {
              ...column,
              renderEditCell: (params) => (
                <SelectSiteParameters
                  {...params}
                  column={column}
                  rowModesModel={rowModesModel}
                  setRowModesModel={setRowModesModel}
                />
              ),
            };
          default:
            return column;
        }
      }
      if (column.renderEditCellProps) {
        if (column.renderEditCellProps.multiple) {
          return {
            ...column,
            renderEditCell: (params) => (
              <CategoricalEditCellMultiSelect
                {...params}
                column={column}
                rowModesModel={rowModesModel}
                setRowModesModel={setRowModesModel}
              />
            ),
          };
        }
        return {
          ...column,
          renderEditCell: (params) => (
            <CategoricalEditCell {...params} column={column} />
          ),
        };
      }
      return column;
    });

  // add any additional columns present in data, except for hidden columns
  columns = [
    ...columns,
    ...Object.keys(data?.[0] || {})
      .filter((key) => !entityConfig?.hiddenColumns?.includes(key))
      .filter(
        (key) =>
          !entityConfig?.columnConfig
            .map((column) => column.field)
            .includes(key)
      )
      .map((key) => ({
        field: key,
        headerName: key,
        editable: true,
      })),
  ];

  // add actions for editing and deleting rows
  columns = [
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id }) => {
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => setDeleteId(id)}
            color="inherit"
          />,
        ];
      },
    },
    ...columns,
  ];

  // hide filter columns with columnVisibilityModel
  let columnVisibilityModel = {};
  entityConfig?.filterOptions?.forEach((filter) => {
    columnVisibilityModel[filter.id] = false;
  });

  React.useEffect(() => {
    const filterItems = getFilterItems();
    setFilterModel({ items: filterItems });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  // delete row, then get updated data and set in redux store
  const handleDeleteClick = (id) => {
    setSnackbarAutoHideDuration(null);
    setSnackbarOpen(true);
    setSnackbarMessage("Deleting...");
    axios
      .delete(`${API_URL}/${entityConfig.endpoint}/${id}`)
      .then((response) => {
        setSnackbarMessage("Deleted!");
        setSnackbarAutoHideDuration(SNACKBAR_AUTO_HIDE_DURATION);
        dispatch(getData({ dataId: selectedEntityId, source: "DataEditor" }));
      })
      .catch((error) => {
        // fetch data again
        dispatch(getData({ dataId: selectedEntityId, source: "DataEditor" }));

        // log the error
        console.log(error);
        setSnackbarMessage(
          "Error deleting record, " + error.response?.data?.message ||
            "please try again later."
        );
        setSnackbarAutoHideDuration(SNACKBAR_AUTO_HIDE_DURATION);
      });
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  // makes PUT request to API to update row
  const updateRow = (updatedRow, originalRow) => {
    // take no action if row is unchanged
    if (JSON.stringify(updatedRow) === JSON.stringify(originalRow)) {
      return updatedRow;
    }
    setSnackbarAutoHideDuration(null);
    setSnackbarOpen(true);
    setSnackbarMessage("Updating...");
    axios
      .put(`${API_URL}/${entityConfig.endpoint}/${updatedRow.id}`, updatedRow)
      .then((response) => {
        setSnackbarMessage("Updated!");
        setSnackbarAutoHideDuration(SNACKBAR_AUTO_HIDE_DURATION);
        dispatch(getData({ dataId: selectedEntityId, source: "DataEditor" }));
      });
    return updatedRow;
  };

  return (
    <>
      <Box
        id="data-grid-container"
        sx={{
          display: "flex",
          flex: 1,
          height: "100%",
          overflowX: "auto",
          "& .Mui-error": {
            backgroundColor: "error.main",
            color: "#000",
          },
        }}
      >
        <RenderConfirmDelete
          deleteId={deleteId}
          handleDeleteClick={handleDeleteClick}
          setDeleteId={setDeleteId}
        />
        <DataGridPro
          rows={rows || []}
          columns={columns || []}
          editMode="row"
          filterModel={filterModel}
          getRowHeight={() => 50}
          getRowSpacing={() => 10}
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
          }}
          onFilterModelChange={(model) => setFilterModel(model)}
          pagination
          pinnedColumns={{ left: ["actions"] }}
          processRowUpdate={(updatedRow, originalRow) =>
            updateRow(updatedRow, originalRow)
          }
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          slots={{
            toolbar: Toolbar,
            footer: CustomFooter,
          }}
          slotProps={{
            toolbar: {
              setRowModesModel,
              setSnackbarAutoHideDuration,
              setSnackbarMessage,
              setSnackbarOpen,
              editable: true,
              showFilters: showFilters,
            },
          }}
          sx={{
            border: 0,
            borderRadius: 0,
            backgroundColor: "primary.light",
            borderColor: "primary.light",

            "& .MuiDataGrid-cell:hover": {
              backgroundColor: "primary.darker",
              cursor: "pointer",
            },
            // adds padding on cells
            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
              py: "8px",
            },
            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
              py: "15px",
            },
            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
              py: "22px",
            },
            // "& .MuiDataGrid-columnHeaders": { display: "none" },
            "& .MuiDataGrid-virtualScroller": { marginTop: "0!important" },
            flex: 1,
          }}
        />
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={snackbarAutoHideDuration}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        action={
          snackbarAutoHideDuration ? null : (
            <CircularProgress
              size={20}
              sx={{ color: "secondary.contrastText" }}
            />
          )
        }
      />
    </>
  );
};

export default DataEditor;
